import React, { useEffect, useState, useCallback, useRef } from "react";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Autocomplete,
  Paper,
  Box,
  Typography,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import muiTheme from "../theme/muiTheme";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";

const PROCESSOS = [
  { value: "PROPOSTA", label: "Proposta" },
  {
    value: "MINUTACONTRATOIMOVEIS",
    label: "Minuta de Contrato de Imóveis",
  },
  { value: "OUTRASMINUTAS", label: "Outras Minutas" },
  { value: "FICHASPESSOAS", label: "Fichas de Pessoas" },
  { value: "ANALISECREDITO", label: "Análise de Crédito" },
  { value: "TEMPLATECONTRATO", label: "Template de Contrato" },
];

const DEPARTAMENTOS = [
  { value: "JURIDICO", label: "Jurídico" },
  { value: "FINANCEIRO", label: "Financeiro" },
  { value: "DIRETORIA", label: "Diretoria" },
  { value: "COMERCIAL", label: "Comercial" },
];

const labelProcesso = (v) => PROCESSOS.find((p) => p.value === v)?.label ?? v;
const labelDepartamento = (v) => DEPARTAMENTOS.find((d) => d.value === v)?.label ?? v ?? "—";

const formVazio = { nome: "", processo: "" };

const proximaOrdemSugerida = (lista) => (lista.length ? Math.max(...lista.map((u) => u.ordem)) + 1 : 1);

export default function AlcadasAprovacao() {
  const [alcadas, setAlcadas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);

  const [inputFiltro, setInputFiltro] = useState("");
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(5);
  const [gridMinimizado, setGridMinimizado] = useState(false);

  const nomeInputRef = useRef(null);

  // form inferior: null = novo, número = id selecionado
  const [editandoId, setEditandoId] = useState(null);
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  const [form, setForm] = useState({ ...formVazio });
  const [formUsuarios, setFormUsuarios] = useState([]); // lista local de aprovadores (salva junto)

  // inline add
  const [novoAprovador, setNovoAprovador] = useState({ usuarioId: null, ordem: String(proximaOrdemSugerida([])), departamento: "" });
  // inline edit (uid = usuarioId do aprovador sendo editado)
  const [editAprovadorUid, setEditAprovadorUid] = useState(null);
  const [editAprovadorForm, setEditAprovadorForm] = useState({ usuarioId: null, ordem: "", departamento: "" });

  const [confirmDelete, setConfirmDelete] = useState({ aberto: false, tipo: null, alcadaId: null, uid: null });

  const [errosForm, setErrosForm] = useState({});

  const [snack, setSnack] = useState({ aberto: false, msg: "", severity: "success" });
  const mostrarSnack = (msg, severity = "success") => setSnack({ aberto: true, msg, severity });

  // permissões
  const token = localStorage.getItem("token");
  let tipoUsuario = "";
  if (token) {
    try {
      const decoded = jwtDecode(token);
      tipoUsuario = decoded.tipo?.toString().trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    } catch (_) {}
  }
  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  const [total, setTotal] = useState(0);

  const carregarAlcadas = useCallback(() => {
    const tk = localStorage.getItem("token");
    const params = new URLSearchParams({ filtro, pagina, porPagina });
    setCarregando(true);
    fetch(`/api/alcadas?${params}`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => r.json())
      .then(({ data, total: t }) => {
        setAlcadas(Array.isArray(data) ? data : []);
        setTotal(t ?? 0);
      })
      .catch((err) => console.error("Erro ao buscar alçadas:", err))
      .finally(() => setCarregando(false));
  }, [filtro, pagina, porPagina]);

  useEffect(() => {
    if (!podeVisualizar) return;
    carregarAlcadas();
  }, [podeVisualizar, carregarAlcadas]);

  // ── Pesquisa automática: aplica o filtro digitado após o usuário parar de digitar
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltro(inputFiltro);
      setPagina(0);
    }, 400);
    return () => clearTimeout(t);
  }, [inputFiltro]);

  const handlePesquisar = () => {
    setFiltro(inputFiltro);
    setPagina(0);
  };
  const handleLimparFiltro = () => {
    setInputFiltro("");
    setFiltro("");
    setPagina(0);
  };

  const handleCopiar = async () => {
    const texto = alcadas.map((alcada) => `${labelProcesso(alcada.processo)} - ${alcada.nome}`).join(", ");

    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }

    const ok = await copiarTexto(texto);

    alert(ok ? "Copiado!" : "Não foi possível copiar.");
  };

  // =======================
  // CSV
  // =======================
  const handleCSV = () => {
    const BOM = "\uFEFF";

    const header = ["Processo", "Quantidade de Usuários", "Usuários", "Nome"];

    const csvEscape = (value) => {
      const texto = String(value ?? "");

      if (/[;"\n\r]/.test(texto)) {
        return `"${texto.replace(/"/g, '""')}"`;
      }

      return texto;
    };

    const linhas = alcadas.map((alcada) => {
      const listaUsuarios = (alcada.usuarios ?? [])
        .slice()
        .sort((a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0))
        .map((u) => {
          const nomeUsuario = u.usuario?.nome || usuarios.find((usuario) => usuario.id === u.usuarioId)?.nome || "—";

          const departamento = labelDepartamento(u.departamento);

          return `${u.ordem}º - ${nomeUsuario} - ${departamento}`;
        })
        .join(" | ");

      return [labelProcesso(alcada.processo), alcada.usuarios?.length ?? 0, listaUsuarios, alcada.nome || ""].map(csvEscape);
    });

    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");

    const blob = new Blob([BOM + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;

    link.setAttribute("download", "alcadas-aprovacao.csv");

    document.body.appendChild(link);

    link.click();

    link.remove();

    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const tk = localStorage.getItem("token");
    fetch("/api/alcadas/usuarios", { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => r.json())
      .then((data) => setUsuarios(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Erro ao buscar usuários:", err));
  }, []);

  // ── Iniciar edição de alçada (preenche form inferior) ─────────────────────
  const handleVisualizar = (alcada) => {
    setEditandoId(alcada.id);
    setSomenteLeitura(true);
    setForm({ nome: alcada.nome, processo: alcada.processo });
    setFormUsuarios(alcada.usuarios ?? []);
    setNovoAprovador({ usuarioId: null, ordem: String(proximaOrdemSugerida(alcada.usuarios ?? [])), departamento: "" });
    setGridMinimizado(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    setTimeout(() => nomeInputRef.current?.focus(), 300);
  };

  const handleHabilitarEdicao = () => {
    setSomenteLeitura(false);
    setTimeout(() => nomeInputRef.current?.focus(), 50);
  };

  const handleCancelarForm = () => {
    setEditandoId(null);
    setSomenteLeitura(false);
    setForm({ ...formVazio });
    setFormUsuarios([]);
    setNovoAprovador({ usuarioId: null, ordem: String(proximaOrdemSugerida([])), departamento: "" });
    setEditAprovadorUid(null);
    setErrosForm({});
    setGridMinimizado(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Salvar (criar ou editar) — envia objeto completo, backend resolve o diff ─
  const handleSalvar = () => {
    const novosErros = {};
    if (!form.nome.trim()) novosErros.nome = "Nome é obrigatório.";
    if (!form.processo) novosErros.processo = "Processo é obrigatório.";
    if (Object.keys(novosErros).length > 0) {
      setErrosForm(novosErros);
      // rola até o primeiro campo inválido
      nomeInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setErrosForm({});
    if (novoAprovador.usuarioId) {
      mostrarSnack("Há um aprovador pendente de adição. Clique em Adicionar ou limpe os campos antes de salvar.", "warning");
      return;
    }
    if (editAprovadorUid !== null) {
      mostrarSnack("Há uma edição de aprovador em andamento. Confirme ou cancele antes de salvar.", "warning");
      return;
    }

    const tk = localStorage.getItem("token");
    const url = editandoId ? `/api/alcadas/${editandoId}` : "/api/alcadas";
    const method = editandoId ? "PUT" : "POST";

    const usuarios = formUsuarios.map((u) => ({
      id: u.id ?? undefined, // undefined é omitido pelo JSON — ausente = novo registro
      usuarioId: u.usuarioId,
      ordem: u.ordem,
      departamento: u.departamento || null,
    }));

    fetch(url, {
      method,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ ...form, usuarios }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json();
          mostrarSnack(d.erro || "Erro.", "error");
          return;
        }
        mostrarSnack(editandoId ? "Alçada atualizada!" : "Alçada criada com sucesso!");
        handleCancelarForm();
        carregarAlcadas();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"));
  };

  // ── Excluir alçada ────────────────────────────────────────────────────────
  const handleExcluirAlcada = () => {
    const { alcadaId } = confirmDelete;
    setConfirmDelete({ aberto: false, tipo: null, alcadaId: null, uid: null });
    const tk = localStorage.getItem("token");
    fetch(`/api/alcadas/${alcadaId}`, { method: "DELETE", headers: { Authorization: `Bearer ${tk}` } })
      .then((res) => {
        if (!res.ok && res.status !== 204) {
          mostrarSnack("Erro ao excluir.", "error");
          return;
        }
        if (editandoId === alcadaId) handleCancelarForm();
        mostrarSnack("Alçada excluída.", "warning");
        carregarAlcadas();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"));
  };

  // ── Adicionar aprovador inline ────────────────────────────────────────────
  const handleAdicionarAprovador = () => {
    if (!novoAprovador.usuarioId) {
      mostrarSnack("Selecione um usuário.", "warning");
      return;
    }
    if (!novoAprovador.ordem) {
      mostrarSnack("Ordem é obrigatória.", "warning");
      return;
    }
    const ordemNum = Number(novoAprovador.ordem);
    if (!Number.isInteger(ordemNum) || ordemNum <= 0) {
      mostrarSnack("Ordem deve ser um número inteiro positivo.", "warning");
      return;
    }
    if (formUsuarios.some((f) => f.ordem === ordemNum)) {
      mostrarSnack(`Ordem ${ordemNum} já está em uso por outro aprovador.`, "warning");
      return;
    }
    if (formUsuarios.some((f) => f.usuarioId === novoAprovador.usuarioId)) {
      mostrarSnack("Usuário já está na lista.", "warning");
      return;
    }
    const usuarioObj = usuarios.find((u) => u.id === novoAprovador.usuarioId);
    const atualizados = [
      ...formUsuarios,
      { id: null, usuarioId: novoAprovador.usuarioId, ordem: ordemNum, departamento: novoAprovador.departamento || null, usuario: usuarioObj },
    ].sort((a, b) => a.ordem - b.ordem);
    setFormUsuarios(atualizados);
    setNovoAprovador({ usuarioId: null, ordem: String(proximaOrdemSugerida(atualizados)), departamento: "" });
  };

  // ── Salvar edição inline de aprovador ─────────────────────────────────────
  const handleSalvarEditAprovador = () => {
    if (!editAprovadorForm.usuarioId) {
      mostrarSnack("Selecione um usuário.", "warning");
      return;
    }
    if (!editAprovadorForm.ordem) {
      mostrarSnack("Ordem é obrigatória.", "warning");
      return;
    }
    const ordemNum = Number(editAprovadorForm.ordem);
    if (!Number.isInteger(ordemNum) || ordemNum <= 0) {
      mostrarSnack("Ordem deve ser um número inteiro positivo.", "warning");
      return;
    }
    if (formUsuarios.some((f) => f.usuarioId !== editAprovadorUid && f.usuarioId === editAprovadorForm.usuarioId)) {
      mostrarSnack("Usuário já está na lista.", "warning");
      return;
    }
    if (formUsuarios.some((f) => f.usuarioId !== editAprovadorUid && f.ordem === ordemNum)) {
      mostrarSnack(`Ordem ${ordemNum} já está em uso por outro aprovador.`, "warning");
      return;
    }
    const usuarioObj = usuarios.find((u) => u.id === editAprovadorForm.usuarioId);
    setFormUsuarios((prev) =>
      prev
        .map((u) =>
          u.usuarioId === editAprovadorUid
            ? { ...u, usuarioId: editAprovadorForm.usuarioId, usuario: usuarioObj, ordem: ordemNum, departamento: editAprovadorForm.departamento || null }
            : u,
        )
        .sort((a, b) => a.ordem - b.ordem),
    );
    setEditAprovadorUid(null);
  };

  // ── Remover aprovador (apenas no estado local) ────────────────────────────
  const handleExcluirVinculo = () => {
    const { uid } = confirmDelete; // uid = usuarioId
    const restantes = formUsuarios.filter((u) => u.usuarioId !== uid);
    setFormUsuarios(restantes);
    setNovoAprovador({ usuarioId: null, ordem: String(proximaOrdemSugerida(restantes)), departamento: "" });
    setConfirmDelete({ aberto: false, tipo: null, alcadaId: null, uid: null });
  };

  const handleConfirmarDelete = (tipo, alcadaId, uid = null) => {
    document.activeElement?.blur();
    setConfirmDelete({ aberto: true, tipo, alcadaId, uid });
  };

  const handleExcluirConfirmado = () => {
    if (confirmDelete.tipo === "alcada") handleExcluirAlcada();
    else handleExcluirVinculo();
  };

  // filtro e paginação resolvidos no backend — alcadas já vem paginado

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "var(--ig-t-ffffff)" }}>
        {/* Título */}
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Alçadas de Aprovação
        </Typography>

        {/* ── Filtro ─────────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 1,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Filtrar Alçada"
            value={inputFiltro}
            onChange={(e) => setInputFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handlePesquisar();
              }
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon
                      sx={{
                        color: "var(--ig-t-9ca3af)",
                        fontSize: 20,
                      }}
                    />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "transparent",
                color: "var(--ig-t-ffffff)",
              },
              "& input::placeholder": {
                color: "var(--ig-t-ffffff)",
                opacity: 1,
                fontWeight: 500,
                fontSize: "1rem",
              },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            type="button"
            onClick={handleLimparFiltro}
            sx={{
              flexShrink: 0,
              width: "auto",
              height: 40,
              color: "#2196f3",
              borderColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#42a5f5",
                backgroundColor: "rgba(33, 150, 243, 0.08)",
              },
            }}
          >
            Limpar Filtro
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCopiar}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            <i
              className="fas fa-copy"
              style={{
                marginRight: 6,
              }}
            />
            Copiar
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCSV}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            CSV
          </Button>
        </Box>

        {/* ── Minimizar grid ───────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1 }}>
          {!gridMinimizado ? (
            <button
              type="button"
              onClick={() => setGridMinimizado(true)}
              style={{
                border: "1px solid #d32f2f",
                color: "#ff4c4c",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setGridMinimizado(false)}
              style={{
                border: "1px solid #1998a8",
                color: "#1998a8",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↗ Expandir
            </button>
          )}
        </Box>

        {/* ── Grid ─────────────────────────────────────────────────────────── */}
        <Collapse in={!gridMinimizado}>
          <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: "transparent", boxShadow: "none" }}>
            {carregando ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Table size="small" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Ações
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Processo
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Usuários
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        width: "100%",
                      }}
                    >
                      Nome
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {alcadas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ color: "var(--ig-t-888888)", py: 3, borderBottom: "1px solid var(--ig-b-444444)" }}>
                        Nenhuma alçada encontrada.
                      </TableCell>
                    </TableRow>
                  ) : (
                    alcadas.map((alcada) => (
                      <TableRow
                        key={alcada.id}
                        sx={{
                          "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" },
                          backgroundColor: editandoId === alcada.id ? "rgba(25,152,168,0.08)" : "transparent",
                        }}
                      >
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                          {podeVisualizar && (
                            <Tooltip title="Visualizar / Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleVisualizar(alcada)}
                                sx={{
                                  backgroundColor: "#1998a8",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",
                                  "&:hover": { backgroundColor: "#1565c0" },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {podeExcluir && (
                            <Tooltip title="Excluir alçada">
                              <IconButton
                                size="small"
                                onClick={() => handleConfirmarDelete("alcada", alcada.id)}
                                sx={{
                                  backgroundColor: "#d32f2f",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",
                                  "&:hover": { backgroundColor: "#c62828" },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)", whiteSpace: "nowrap" }}>
                          {labelProcesso(alcada.processo)}
                        </TableCell>
                        <TableCell align="center" sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-aaaaaa)", whiteSpace: "nowrap" }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                            <PeopleAltIcon fontSize="small" />
                            <span>{alcada.usuarios?.length ?? 0}</span>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)", width: "100%" }}>{alcada.nome}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}

            {/* Paginação */}
            {!carregando && (
              <TablePagination
                component="div"
                count={total}
                page={pagina}
                onPageChange={(_, p) => setPagina(p)}
                rowsPerPage={porPagina}
                onRowsPerPageChange={(e) => {
                  setPorPagina(parseInt(e.target.value, 10));
                  setPagina(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Alçadas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                sx={{
                  color: "var(--ig-t-ffffff)",
                  borderTop: "1px solid var(--ig-b-444444)",
                  "& .MuiTablePagination-selectIcon": { color: "var(--ig-t-ffffff)" },
                  "& .MuiTablePagination-actions button": { color: "var(--ig-t-ffffff)", width: "auto" },
                  "& .MuiTablePagination-actions": { display: "flex", alignItems: "center" },
                }}
              />
            )}
          </TableContainer>
        </Collapse>

        {/* ── Formulário inferior (visualizar / criar / editar) ────────────── */}
        {(podeCadastrar || (podeVisualizar && editandoId)) && (
          <Paper sx={{ p: 3, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, boxShadow: "0 0 20px rgba(0,0,0,0.4)" }}>
            <Typography variant="h6" sx={{ mb: 2, color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
              {editandoId && somenteLeitura ? "Visualizar Alçada" : editandoId ? "Editar Alçada" : "Nova Alçada de Aprovação"}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Nome da Alçada*"
                size="small"
                fullWidth
                inputRef={nomeInputRef}
                value={form.nome}
                onChange={(e) => {
                  if (!somenteLeitura) {
                    setForm((p) => ({ ...p, nome: e.target.value.toUpperCase() }));
                    if (errosForm.nome) setErrosForm((p) => ({ ...p, nome: undefined }));
                  }
                }}
                onBlur={() => {
                  if (!somenteLeitura && !form.nome.trim()) setErrosForm((p) => ({ ...p, nome: "Nome é obrigatório." }));
                }}
                error={!!errosForm.nome}
                helperText={errosForm.nome}
                slotProps={{ input: { readOnly: somenteLeitura }, htmlInput: { maxLength: 200 } }}
                sx={{
                  "& .MuiInputLabel-root": { color: errosForm.nome ? "#f44336" : "var(--ig-t-ffffff)", fontWeight: "bold" },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: somenteLeitura ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
                    color: "var(--ig-t-ffffff)",
                    "& fieldset": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : errosForm.nome ? "#f44336" : "var(--ig-b-444444)" },
                    "&:hover fieldset": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : errosForm.nome ? "#f44336" : "var(--ig-b-888888)" },
                  },
                  "& input": { color: "var(--ig-t-ffffff)" },
                  "& .MuiFormHelperText-root": { color: "#f44336" },
                }}
              />

              <FormControl size="small" fullWidth error={!!errosForm.processo}>
                <InputLabel sx={{ color: errosForm.processo ? "#f44336" : "var(--ig-t-ffffff)", fontWeight: "bold" }}>Processo*</InputLabel>
                <Select
                  label="Processo*"
                  value={form.processo}
                  onChange={(e) => {
                    if (!somenteLeitura) {
                      setForm((p) => ({ ...p, processo: e.target.value }));
                      if (errosForm.processo) setErrosForm((p) => ({ ...p, processo: undefined }));
                    }
                  }}
                  onBlur={() => {
                    if (!somenteLeitura && !form.processo) setErrosForm((p) => ({ ...p, processo: "Processo é obrigatório." }));
                  }}
                  inputProps={{ readOnly: somenteLeitura }}
                  sx={{
                    backgroundColor: somenteLeitura ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
                    color: "var(--ig-t-ffffff)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: somenteLeitura ? "var(--ig-b-333333)" : errosForm.processo ? "#f44336" : "var(--ig-b-444444)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: somenteLeitura ? "var(--ig-b-333333)" : errosForm.processo ? "#f44336" : "var(--ig-b-888888)",
                    },
                    "& .MuiSvgIcon-root": { color: somenteLeitura ? "transparent" : "var(--ig-t-ffffff)" },
                  }}
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  {PROCESSOS.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
                {errosForm.processo && <FormHelperText sx={{ color: "#f44336" }}>{errosForm.processo}</FormHelperText>}
              </FormControl>

              {/* Seção de aprovadores — inline add/edit, salva junto com o form principal */}
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ color: "var(--ig-t-cccccc)", fontWeight: 600, mb: 1 }}>
                  Aprovadores
                </Typography>

                <Table size="small" sx={{ width: "100%" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "var(--ig-f-252525)",
                          color: "var(--ig-t-aaaaaa)",
                          fontWeight: "bold",
                          borderBottom: "1px solid var(--ig-b-444444)",
                          py: 0.8,
                          whiteSpace: "nowrap",
                          width: "90px",
                        }}
                      >
                        Ordem
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "var(--ig-f-252525)",
                          color: "var(--ig-t-aaaaaa)",
                          fontWeight: "bold",
                          borderBottom: "1px solid var(--ig-b-444444)",
                          py: 0.8,
                          width: "45%",
                        }}
                      >
                        Usuário
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "var(--ig-f-252525)",
                          color: "var(--ig-t-aaaaaa)",
                          fontWeight: "bold",
                          borderBottom: "1px solid var(--ig-b-444444)",
                          py: 0.8,
                          width: "25%",
                        }}
                      >
                        Departamento
                      </TableCell>
                      {!somenteLeitura && podeCadastrar && (
                        <TableCell
                          align="right"
                          sx={{
                            backgroundColor: "var(--ig-f-252525)",
                            color: "var(--ig-t-aaaaaa)",
                            fontWeight: "bold",
                            borderBottom: "1px solid var(--ig-b-444444)",
                            py: 0.8,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Ações
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Linha informativa quando não há aprovadores */}
                    {formUsuarios.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={!somenteLeitura && podeCadastrar ? 4 : 3}
                          sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-666666)", fontStyle: "italic", py: 1.5, textAlign: "center" }}
                        >
                          Nenhum aprovador adicionado.
                        </TableCell>
                      </TableRow>
                    )}
                    {formUsuarios.map((u) =>
                      editAprovadorUid === u.usuarioId ? (
                        /* ── Linha de edição inline ── */
                        <TableRow key={u.usuarioId} sx={{ backgroundColor: "rgba(25,152,168,0.08)" }}>
                          <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={editAprovadorForm.ordem}
                              onChange={(e) => setEditAprovadorForm((p) => ({ ...p, ordem: e.target.value }))}
                              slotProps={{ input: { min: 1 } }}
                              sx={{
                                minWidth: 80,
                                "& .MuiOutlinedInput-root": {
                                  color: "var(--ig-t-ffffff)",
                                  backgroundColor: "var(--ig-f-2a2a2a)",
                                  "& fieldset": { borderColor: "var(--ig-b-444444)" },
                                },
                                "& input": { color: "var(--ig-t-ffffff)" },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                            <Autocomplete
                              size="small"
                              disableClearable
                              options={usuarios.filter((us) => us.id === editAprovadorUid || !formUsuarios.some((f) => f.usuarioId === us.id))}
                              getOptionLabel={(us) => us.nome}
                              value={usuarios.find((us) => us.id === editAprovadorForm.usuarioId) || null}
                              onChange={(_, val) => setEditAprovadorForm((p) => ({ ...p, usuarioId: val?.id ?? null }))}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Selecionar usuário"
                                  size="small"
                                  sx={{
                                    minWidth: 240,
                                    "& .MuiOutlinedInput-root": {
                                      color: "var(--ig-t-ffffff)",
                                      backgroundColor: "var(--ig-f-2a2a2a)",
                                      "& fieldset": { borderColor: "var(--ig-b-444444)" },
                                    },
                                    "& input": { color: "var(--ig-t-ffffff)" },
                                  }}
                                />
                              )}
                              sx={{ minWidth: 240 }}
                            />
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                              <Select
                                value={editAprovadorForm.departamento}
                                onChange={(e) => setEditAprovadorForm((p) => ({ ...p, departamento: e.target.value }))}
                                displayEmpty
                                sx={{
                                  color: "var(--ig-t-ffffff)",
                                  backgroundColor: "var(--ig-f-2a2a2a)",
                                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--ig-b-444444)" },
                                  "& .MuiSvgIcon-root": { color: "var(--ig-t-ffffff)" },
                                }}
                              >
                                <MenuItem value="">— Nenhum —</MenuItem>
                                {DEPARTAMENTOS.map((d) => (
                                  <MenuItem key={d.value} value={d.value}>
                                    {d.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="right" sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5, whiteSpace: "nowrap" }}>
                            <Tooltip title="Confirmar edição">
                              <IconButton
                                size="small"
                                onClick={handleSalvarEditAprovador}
                                sx={{
                                  backgroundColor: "#2e7d32",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",
                                  "&:hover": { backgroundColor: "#1b5e20" },
                                }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancelar edição">
                              <IconButton
                                size="small"
                                onClick={() => setEditAprovadorUid(null)}
                                sx={{
                                  backgroundColor: "#555",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",
                                  "&:hover": { backgroundColor: "#444" },
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ) : (
                        /* ── Linha normal ── */
                        <TableRow key={u.usuarioId} sx={{ "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" } }}>
                          <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-ffffff)", py: 0.8 }}>{u.ordem}</TableCell>
                          <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-ffffff)", py: 0.8 }}>
                            {u.usuario?.nome ?? "—"}
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-cccccc)", py: 0.8 }}>
                            {labelDepartamento(u.departamento)}
                          </TableCell>
                          {!somenteLeitura && podeCadastrar && (
                            <TableCell align="right" sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.8, whiteSpace: "nowrap" }}>
                              <Tooltip
                                title={editAprovadorUid !== null ? "Confirme ou cancele a edição em andamento antes de editar outro" : "Editar aprovador"}
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={editAprovadorUid !== null}
                                    onClick={() => {
                                      setEditAprovadorUid(u.usuarioId);
                                      setEditAprovadorForm({ usuarioId: u.usuarioId, ordem: u.ordem, departamento: u.departamento || "" });
                                    }}
                                    sx={{
                                      backgroundColor: editAprovadorUid !== null ? "var(--ig-f-333333)" : "#1998a8",
                                      color: "#fff",
                                      borderRadius: "4px",
                                      mx: 0.25,
                                      width: "auto",
                                      "&:hover": { backgroundColor: editAprovadorUid !== null ? "var(--ig-f-333333)" : "#1565c0" },
                                      "&.Mui-disabled": { color: "var(--ig-t-666666)" },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Remover aprovador">
                                <IconButton
                                  size="small"
                                  onClick={() => handleConfirmarDelete("vinculo", editandoId, u.usuarioId)}
                                  sx={{
                                    backgroundColor: "#d32f2f",
                                    color: "#fff",
                                    borderRadius: "4px",
                                    mx: 0.25,
                                    width: "auto",
                                    "&:hover": { backgroundColor: "#c62828" },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ),
                    )}

                    {/* ── Linha para adicionar novo aprovador ── */}
                    {!somenteLeitura && podeCadastrar && (
                      <TableRow sx={{ backgroundColor: "var(--ig-f-1a1a1a)" }}>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={novoAprovador.ordem}
                            onChange={(e) => setNovoAprovador((p) => ({ ...p, ordem: e.target.value }))}
                            placeholder="Nº"
                            slotProps={{ input: { min: 1 } }}
                            sx={{
                              minWidth: 80,
                              "& .MuiOutlinedInput-root": {
                                color: "var(--ig-t-ffffff)",
                                backgroundColor: "var(--ig-f-2a2a2a)",
                                "& fieldset": { borderColor: "var(--ig-b-444444)" },
                              },
                              "& input": { color: "var(--ig-t-ffffff)" },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                          <Autocomplete
                            size="small"
                            options={usuarios.filter((u) => !formUsuarios.some((f) => f.usuarioId === u.id))}
                            getOptionLabel={(u) => u.nome}
                            value={usuarios.find((u) => u.id === novoAprovador.usuarioId) || null}
                            onChange={(_, val) => setNovoAprovador((p) => ({ ...p, usuarioId: val?.id ?? null }))}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Selecionar usuário"
                                size="small"
                                sx={{
                                  minWidth: 240,
                                  "& .MuiOutlinedInput-root": {
                                    color: "var(--ig-t-ffffff)",
                                    backgroundColor: "var(--ig-f-2a2a2a)",
                                    "& fieldset": { borderColor: "var(--ig-b-444444)" },
                                  },
                                  "& input": { color: "var(--ig-t-ffffff)" },
                                }}
                              />
                            )}
                            sx={{ minWidth: 240 }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5 }}>
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                              value={novoAprovador.departamento}
                              onChange={(e) => setNovoAprovador((p) => ({ ...p, departamento: e.target.value }))}
                              displayEmpty
                              sx={{
                                color: "var(--ig-t-ffffff)",
                                backgroundColor: "var(--ig-f-2a2a2a)",
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--ig-b-444444)" },
                                "& .MuiSvgIcon-root": { color: "var(--ig-t-ffffff)" },
                              }}
                            >
                              <MenuItem value="">— Nenhum —</MenuItem>
                              {DEPARTAMENTOS.map((d) => (
                                <MenuItem key={d.value} value={d.value}>
                                  {d.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: "1px solid var(--ig-b-333333)", py: 0.5, whiteSpace: "nowrap" }}>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleAdicionarAprovador}
                            sx={{
                              backgroundColor: "#1998a8",
                              color: "#fff",
                              "&:hover": { backgroundColor: "#1565c0" },
                              textTransform: "none",
                              fontWeight: "bold",
                              width: "auto",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Adicionar
                          </Button>
                          {(novoAprovador.usuarioId || novoAprovador.departamento) && (
                            <Tooltip title="Limpar campos">
                              <IconButton
                                size="small"
                                onClick={() => setNovoAprovador({ usuarioId: null, ordem: String(proximaOrdemSugerida(formUsuarios)), departamento: "" })}
                                sx={{
                                  ml: 0.5,
                                  backgroundColor: "#555",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  width: "auto",
                                  "&:hover": { backgroundColor: "#444" },
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                {/* Modo visualização */}
                {editandoId && somenteLeitura && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleCancelarForm}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",
                        "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                        fontWeight: "bold",
                        px: 3,
                      }}
                    >
                      Fechar
                    </Button>
                    {podeCadastrar && (
                      <Button
                        variant="contained"
                        startIcon={<FaEdit />}
                        onClick={handleHabilitarEdicao}
                        sx={{ width: "auto", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold", py: 1, px: 3 }}
                      >
                        Editar
                      </Button>
                    )}
                    {podeExcluir && (
                      <Button
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleConfirmarDelete("alcada", editandoId)}
                        sx={{ width: "auto", backgroundColor: "#d32f2f", "&:hover": { backgroundColor: "#c62828" }, fontWeight: "bold", py: 1, px: 3 }}
                      >
                        Excluir
                      </Button>
                    )}
                  </>
                )}
                {/* Modo edição / criação */}
                {(!editandoId || !somenteLeitura) && (
                  <>
                    {editandoId && (
                      <Button
                        variant="outlined"
                        startIcon={<FaTimes />}
                        onClick={handleCancelarForm}
                        sx={{
                          width: "auto",
                          borderColor: "var(--ig-b-555555)",
                          color: "var(--ig-t-cccccc)",
                          "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                          fontWeight: "bold",
                          px: 3,
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                    {podeCadastrar && (
                      <Button
                        variant="contained"
                        startIcon={<FaSave />}
                        onClick={handleSalvar}
                        sx={{ width: "auto", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" }, fontWeight: "bold", py: 1, px: 4 }}
                      >
                        {editandoId ? "Atualizar" : "Cadastrar"}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* ── Dialog: Confirmação de exclusão ──────────────────────────────── */}
      <Dialog open={confirmDelete.aberto} onClose={() => setConfirmDelete({ aberto: false, tipo: null, alcadaId: null, uid: null })}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "var(--ig-t-cccccc)" }}>
            {confirmDelete.tipo === "alcada"
              ? "Deseja excluir esta alçada e todos os seus usuários? Esta ação não pode ser desfeita."
              : "Deseja remover este usuário da alçada?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmDelete({ aberto: false, tipo: null, alcadaId: null, uid: null })}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleExcluirConfirmado}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.aberto}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, aberto: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, aberto: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
